import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { Lock, User, Users, Eye, EyeOff  } from 'lucide-react';

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // Added state for password visibility
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
        
    try {
      // Create Basic Auth header
      const authHeader = 'Basic ' + btoa(`${username}:${password}`);
          
      // We'll determine the user's role automatically by trying to access the /me endpoint
      // Admin users get 400 when accessing /me, employees get 200 or 404
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const userInfoApiUrl = BACKEND_URL ? `${BACKEND_URL}/api/employees/me` : '/api/employees/me';
      const userInfoResponse = await fetch(userInfoApiUrl, {
        headers: {
          'Authorization': authHeader
        }
      });
          
      if (userInfoResponse.status === 200) {
        // Employee authentication successful
        onLogin({ header: authHeader, role: 'employee' });
      } else if (userInfoResponse.status === 400) {
        // Admin authentication successful (admin users get 400 when accessing /me)
        onLogin({ header: authHeader, role: 'admin' });
      } else if (userInfoResponse.status === 401) {
        // Unauthorized - wrong credentials
        toast({
          title: "Помилка авторизації",
          description: "Невірне ім'я користувача або пароль",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Помилка",
          description: "Щось пішло не так. Спробуйте ще раз.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Помилка мережі",
        description: "Не вдалося підключитися до сервера",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Вхід в систему</CardTitle>
          <p className="text-gray-600">Введіть облікові дані</p>
        </CardHeader>
        <CardContent>         
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium text-gray-700">
                Ім'я користувача
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10"
                  placeholder="Ваше ім'я користувача або 'admin'"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-gray-700">
                Пароль
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              variant="default"
            >
              {loading ? 'Вхід...' : 'Увійти'}
            </Button>
          </form>
          
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>Введіть ваші облікові дані (співробітника або адміністратора)</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginForm;